/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint max-statements: ["error", 40] */

import ErrorName from '../error/error-name';
import { errorWithLevelWarning } from '../error/error-with-level';

/**
 * @import AutorebootReplacer from '../autoreboot/autoreboot-replacer'
 * @import { ErrorWithLevel } from '../error/error-with-level'
 * @import { Logger } from '../logger/logger'
 * @import ReplacerControl from './control/replacer-control'
 * @import ReplacerPower from './power/replacer-power'
 * @import ReplacerReboot from './reboot/replacer-reboot'
 * @import ReplacerScreen from './screen/replacer-screen'
 * @import ReplacerSound from './sound/replacer-sound'
 */

/**
 * @typedef {Pick<import('../content/info/content-info').ContentInfo,
 *   | 'backlight'
 *   | 'communicatorSettings'
 *   | 'holidayIntervals'
 *   | 'orientationEnum'
 *   | 'power'
 *   | 'rebootTasks'
 *   | 'volume'
 * >} IContentInfo
 */

/**
 * Nahrazovač obsahu.
 */
export default class Replacer {
  /**
   * Vytvoří nahrazovač obsahu.
   * @param {AutorebootReplacer} autorebootReplacer
   * @param {Logger} logger
   * @param {ReplacerControl} replacerControl
   * @param {ReplacerPower} replacerPower
   * @param {ReplacerReboot} replacerReboot
   * @param {ReplacerScreen} replacerScreen
   * @param {ReplacerSound} replacerSound
   */
  constructor(
    autorebootReplacer,
    logger,
    replacerControl,
    replacerPower,
    replacerReboot,
    replacerScreen,
    replacerSound
  ) {
    this.autorebootReplacer = autorebootReplacer;

    this.logger = logger;
    this.replacerControl = replacerControl;
    this.replacerPower = replacerPower;
    this.replacerReboot = replacerReboot;
    this.replacerScreen = replacerScreen;
    this.replacerSound = replacerSound;
  }

  /**
   * Aplikuje obsah na zařízení.
   * V této akci se synchronizuje nastavení zařízení s obsahem.
   * Jde o čistě stavové operace, není zde žádný výsledek,
   * pouze se nastaví to, co je požadováno v obsahu.
   *
   * Vždy se vyřeší, případná chyba jednoho replaceru nepřeruší ostatní
   * a případné chyby se logují (na jedno volání applyContent může přijít více
   * chyb), ale NotSupportedError chyby jsou ošetřeny jako warning, aby nepřišly
   * na Sentry.
   *
   * TODO: Proč nějaké replacery logují jako warn nepodporu operací uvnitř
   * a některé to nedělají a tím pádem se to zachytí zde a zaloguje jako
   * error s levelem warning? Chtělo by to sjednotit na jednom stylu.
   * @param {IContentInfo} contentInfo
   * @returns {Promise<void>}
   */
  applyContent(contentInfo) {
    return new Promise((resolve) =>
      resolve(
        // Seznam akcí, které se mají provést.
        [
          () => this.autorebootReplacer.replace(contentInfo),
          () => this.replacerControl.replace(contentInfo),
          () => this.replacerSound.replace(contentInfo),
          () => this.replacerPower.replace(contentInfo),
          () => this.replacerReboot.replace(contentInfo),
          () => this.replacerScreen.replace(contentInfo)
        ]
          // Sériové volání všech akcí.
          .reduce(
            (acc, callback) =>
              acc.then(() =>
                callback()
                  // Selhání jedné akce, nepřeruší spuštění ostatních.
                  .catch((error) => {
                    let errorWithLevel = /** @type {ErrorWithLevel} */ (error);
                    // Podľa #534 sa posiela NotSupportedError vyvolaný .setVolume()
                    if (errorWithLevel.name === ErrorName.NotSupportedError) {
                      errorWithLevel = errorWithLevelWarning(error);
                    }
                    this.logger.error(errorWithLevel);
                  })
              ),
            Promise.resolve()
          )
      )
    );
  }
}
